import { useQuery } from 'react-query';
import { useMemo } from 'react';
import {
  useCategories,
  useExtendedParticipantFieldsData,
  useParticipantFieldsData,
} from './data';
import {
  ExtendedParticipantField,
  ParticipantField,
  ParticipantFieldCategory,
  ParticipantFieldCategoryConfig,
} from './types';
import { LocalizedCountry, useCountries } from '../geolocation';

export * from './types';

const addCountriesToField = <T extends ExtendedParticipantField>(
  fields: T[],
  countries: LocalizedCountry[] = [],
): T[] => {
  const choices = countries.map((c: LocalizedCountry) => ({
    label: c.text || c.name || c.nativeName,
    value: c.key,
  }));

  return fields.map((field) => {
    if (field.name === 'countryKey') {
      return {
        ...field,
        choices,
      };
    }
    return field;
  });
};

export const useParticipantFields = () => {
  const { data: countries, isLoading } = useCountries();
  const participantFields = useParticipantFieldsData();
  return useQuery<ParticipantField[]>(
    ['participantFields'],
    () => addCountriesToField(participantFields, countries),
    { enabled: !isLoading },
  );
};
export const useParticipantFieldsCategories = () => {
  const categories = useCategories();
  return useQuery<ParticipantFieldCategoryConfig[]>(
    ['participantFields', 'categories'],
    () => categories,
  );
};

export const useParticipantFieldsCategory = (
  category: ParticipantFieldCategory,
) => {
  const categories = useCategories();
  return categories.find((c) => c.name === category);
};

export const useExtendedParticipantFields = () => {
  const { data: countries, isLoading } = useCountries();
  const extendedParticipantFields = useExtendedParticipantFieldsData();
  return useQuery<ExtendedParticipantField[]>(
    ['extendedParticipantFields'],
    () => addCountriesToField(extendedParticipantFields, countries),
    { enabled: !isLoading },
  );
};

export const useParticipantFieldsAsSelectOptions = () => {
  const { data: fields } = useParticipantFields();
  return useMemo(() => {
    if (!fields) return [];
    return fields.map((config) => ({
      value: config.name,
      label: config.label,
    }));
  }, [fields]);
};

export const useParticipantFieldsByName = () => {
  const { data: fields = [] } = useParticipantFields();
  return useMemo(
    () =>
      fields.reduce((acc, field) => {
        acc[field.name as string] = field;
        return acc;
      }, {} as { [name: string]: ParticipantField }),
    [fields],
  );
};

export const useParticipantFieldsByCategory = () => {
  const { data: fields = [] } = useParticipantFields();
  return useMemo(
    () =>
      fields.reduce((acc, field) => {
        if (!acc[field.category]) {
          acc[field.category] = [];
        }
        acc[field.category].push(field);
        return acc;
      }, {} as Record<ParticipantFieldCategory, ParticipantField[]>),
    [fields],
  );
};

export const useExtendedParticipantFieldsByCategory = () => {
  const { data: fields = [] } = useExtendedParticipantFields();
  return useMemo(
    () =>
      fields.reduce((acc, field) => {
        if (!acc[field.category]) {
          acc[field.category] = [];
        }
        acc[field.category].push(field);
        return acc;
      }, {} as { [category: string]: ExtendedParticipantField[] }),
    [fields],
  );
};

export const useParticipantFieldsByProfileCategory = () => {
  const { data: fields = [] } = useParticipantFields();
  return useMemo(
    () =>
      fields.reduce((acc, field) => {
        if (!acc[field.profileCategory]) {
          acc[field.profileCategory] = [];
        }
        acc[field.profileCategory].push(field);
        return acc;
      }, {} as { [category: string]: ParticipantField[] }),
    [fields],
  );
};
